import dayjs from 'dayjs';
import Inherit from '../../../core/inherit';
import ShelfController from '../../../core/shelf.controller';
import StoreDelegate from './store.delegate';
import { productList } from '../../common';
import { calculateAuditDuration, calculateAverageAuditTime } from '../../../utils/timeUtils';
import { User } from '../../../data';
import { canSeePrices } from '../../../core/access';

class StoreController extends Inherit(ShelfController, StoreDelegate) {
    shouldComponentUpdate(nextProps, nextState) {
        return (
            this.state.store !== nextState.store ||
            this.state.storeChecks !== nextState.storeChecks ||
            this.state.loading !== nextState.loading ||
            this.state.elementsLoading !== nextState.elementsLoading ||
            this.state.prices !== nextState.prices ||
            this.state.contracts !== nextState.contracts ||
            this.state.campaigns !== nextState.campaigns ||
            this.state.analysisRange !== nextState.analysisRange
        );
    }

    async initialize() {
        let store = {};
        let storeChecks = [];
        let prices = [];
        let contracts = [];
        let campaigns = [];

        const today = dayjs();
        const storeId = this.props.searchParams.get('id');

        if (this.isAuthenticated) {
            store = await this.getStoreInfo();
            storeChecks = (await this.fetchStoreChecks(today.startOf('month'), today.endOf('month'), true)) ?? [];
            if (Array.isArray(storeChecks) && storeChecks.length > 0) {
                const lastCheck = storeChecks[0]; // Get the most recent check (now first in sorted array)
                prices = (await this.fetchPriceList(store?._id, lastCheck)) ?? [];
            }
            if (storeId) {
                const [rawContracts, rawCampaigns] = await Promise.all([
                    this.data.fetchContractsByStore(storeId).catch(() => []),
                    this.data.fetchCampaignsByStore(storeId).catch(() => []),
                ]);
                contracts = Array.isArray(rawContracts) ? rawContracts : [];
                campaigns = Array.isArray(rawCampaigns) ? rawCampaigns : [];
            }
        } else this.navigateLogin();

        this.setState({ store, storeChecks, prices, contracts, campaigns, loading: false, elementsLoading: false });
    }

    // Price table / margins are hidden for roles without "Ver precios" (same rule as the store check page).
    get canSeePrices() {
        return canSeePrices(User.getInstance()?.userType);
    }

    /** Most recent store check in the loaded range (by end time, else creation time). */
    lastStoreCheck() {
        const { storeChecks } = this.state;
        if (!storeChecks?.length) return null;
        const when = (c) => new Date(c?.endTime || c?.createdAt || 0).getTime();
        return storeChecks.reduce((latest, c) => (when(c) > when(latest) ? c : latest), storeChecks[0]);
    }

    /** "Período de análisis" change: reload the store checks for the picked range. */
    changeAnalysisPeriod(dates) {
        if (dates && dates[0] && dates[1]) {
            // Keep the page mounted (only the sections reload) so the picker keeps the chosen range.
            this.setState({ analysisRange: dates, elementsLoading: true });
            this.fetchStoreChecks(dates[0].format('YYYY-MM-DD'), dates[1].format('YYYY-MM-DD'));
        }
    }

    async fetchPriceList(storeId, storeCheck) {
        if (!storeId) return {};
        const store = await this.data.fetchStoreById(storeId);

        if (!store) return {};

        // The store's own (per-piso) price list when it has one, else its client's / store type's.
        const dateOfLastCheck = dayjs(storeCheck?.endTime).format('YYYY-MM-DD');
        const priceResponse = await this.data.fetchPricesForStore(store._id || storeId, dateOfLastCheck);
        if (!priceResponse || !Array.isArray(priceResponse.products)) return [];

        const filteredProds = storeCheck?.products.filter((prod) => prod?.isAvailable);

        const priceData = await Promise.all(
            filteredProds?.map((prod) => {
                const priceValue = productList?.find((p) => p?.name === prod?.name)?.value ?? '';
                const price = priceResponse?.products?.find((p) => priceValue === p?.productId) ?? {};

                const margin = prod?.price ? ((prod?.price - price?.price) / prod?.price) * 100 : 0;
                const suggestedMargin = price?.suggestedPrice ? ((price?.suggestedPrice - price?.price) / price?.suggestedPrice) * 100 : 0;
                const priceVar = price?.suggestedPrice && prod?.price ? (prod?.price / price?.suggestedPrice - 1) * 100 : 0;

                return {
                    name: prod?.name,
                    storePrice: isNaN(prod?.price) ? '0.0' : prod?.price,
                    margin: `${isNaN(margin) ? '0.0' : margin?.toFixed(2)}%`,
                    suggestedPrice: isNaN(price?.suggestedPrice) ? '0.0' : price?.suggestedPrice,
                    suggestedMargin: `${isNaN(suggestedMargin) ? '0.0' : suggestedMargin?.toFixed(2)}%`,
                    priceVar: `${isNaN(priceVar) ? '0.0' : priceVar?.toFixed(2)}%`,
                    isPromo: prod?.isPromo ? 'Si' : 'No'
                };
            })
        );

        return priceData;
    }

    async getStoreInfo() {
        let client = null;
        let storeType = null;
        let asesores = [];
        let gerente = null;

        const store = (await this.data.fetchStoreById(this.props.searchParams.get('id'))) || {};
        if (store) client = (await this.data.fetchClientById(store?.client ?? '')) || {};

        if (client) {
            const value = client?.channel === 'moderno' ? client?._id : store?.storeType;

            storeType = await this.data.fetchStoreTypeByValue(value);
            gerente = await this.data.fetchUserById(client?.gerente ?? '');
            asesores = (await Promise.all(client.asesor.map((id) => this.data.fetchUserById(id)))) || [];

            return {
                ...store,
                channel: client.channel,
                storeType: storeType,
                asesor: asesores,
                gerente: gerente?.name,
                products: store?.products
            };
        }
    }

    async fetchStoreChecks(startDate, endDate, isInitial = false) {
        try {
            const storechecks = (await this.data.fetchMonthlyStoreChecks(this.props.searchParams.get('id'), startDate, endDate)) ?? [];
            if (!storechecks?.length > 0 || !storechecks) {
                this.setState({ storeChecks: [], prices: [], elementsLoading: false, loading: false });
                return [];
            }

            const users = new Map((await this.data.fetchUsers())?.map((user) => [user._id, user]));
            const store = await this.data.fetchStoreById(this.props.searchParams.get('id'));

            const checks = await Promise.all(
                storechecks?.map(async (check) => {
                    const user = users.get(check?.userId);

                    // Use shared utility for time calculation (business logic in utils)
                    const totalTime = calculateAuditDuration(check);

                    return {
                        ...check,
                        userName: user?.name,
                        totalTime,
                        storeName: store?.name,
                        storeType: store?.storeType
                    };
                }) ?? []
            );

            // Sort by createdAt from newest to oldest
            const sortedChecks = checks.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

            // Pre-calculate average audit time (business logic in controller)
            const avgAuditTime = calculateAverageAuditTime(sortedChecks);

            if (isInitial) return sortedChecks ?? [];
            else {
                let prices = null;
                if (Array.isArray(sortedChecks) && sortedChecks.length > 0) {
                    const lastCheck = sortedChecks[0]; // Get the most recent check (now first in array)
                    prices = (await this.fetchPriceList(store?._id, lastCheck)) ?? [];
                }
                this.setState({ prices, storeChecks: sortedChecks ?? [], avgAuditTime, elementsLoading: false, loading: false });
            }
        } catch (err) {
            console.log(err);
        }
    }
}

export default StoreController;
